/* eslint-disable import/prefer-default-export */
import cloneDeep from 'lodash.clonedeep';
import i18n from 'i18n';
import {
  FormTemplate,
  FormTemplateMetaData,
  FormTemplateOption,
  FormTemplateSection,
  FormTemplateSectionMetadataLayout,
} from 'data/template/template.types';
import { AccessFunction } from 'data/accessFunction/accessFunction.types';
import { CollectionFunction } from 'data/collectionFunction/collectionFunction.types';
import { CollectionFunctionGroup } from 'data/collectionFunctionGroup/collectionFunctionGroup.types';
import { UtimacoCircuitSwitch } from 'data/utimacoCircuitSwitch/utimacoCircuitSwitch.types';

export type ListenersIpAddressPortsType = {
  [key:string]: string[],
}

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  (clonedTemplate.main as FormTemplateSection).fields.SS8ID.hide = (mode === 'create') ?
  'true' :
  'false';

  (clonedTemplate.main as FormTemplateSection).fields.CCCMCID.hide = (mode === 'create') ?
    'true' :
    'false';

  if (mode === 'edit') {
    (clonedTemplate.main as FormTemplateSection).fields.AFID.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.CFID.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.LEA.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.CCCMCID.readOnly = true;
  } else if (mode === 'view') {
    setAllFieldsAsReadOnlyExcludingFields(clonedTemplate, []);
    (((clonedTemplate.main as FormTemplateSection)
      .metadata as FormTemplateMetaData).layout as FormTemplateSectionMetadataLayout).rows = [
        ['id'],
        ['AFID', 'CFID', 'ISDN'],
        ['LEA', 'CCCMCID', 'CUGILC'],
        ['CUGDNIC', 'CPRISDN', '$empty'],
        ['SS8ID']
    ];
  }

  return clonedTemplate;
}

export function adaptTemplateOptions(
  template: FormTemplate | undefined,
  accessFunctions: AccessFunction[],
  collectionFunctions: CollectionFunction[],
  collectionFunctionGroups: CollectionFunctionGroup[],
  formData?: Partial<UtimacoCircuitSwitch>,
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  (clonedTemplate.main as FormTemplateSection).fields.AFID.options = getAccessFunctionsOptions(accessFunctions);
  (clonedTemplate.main as FormTemplateSection).fields.CFID.options = getCollectionFunctionOptions(collectionFunctions);

  if (formData) {
    const lea = formData.LEA || '';

    (clonedTemplate.main as FormTemplateSection).fields.LEA.options = [
      {
        label: lea,
        value: lea,
      },
    ];

    (clonedTemplate.main as FormTemplateSection).fields.LEA.initial = lea;
  }

  return clonedTemplate;
}

function setAllFieldsAsReadOnlyExcludingFields(template: FormTemplate, excludingFields: string[]) {
  Object.entries(template).forEach(([_templateKey, templateEntry]) => {
    if (typeof templateEntry === 'string') {
      return;
    }
    Object.entries(templateEntry.fields).forEach(([fieldKey, fieldEntry]) => {
      if (!excludingFields.includes(fieldKey)) {
        fieldEntry.readOnly = true;
      }
    });
  });
}

function getAccessFunctionsOptions(
  accessFunctions: AccessFunction[],
): FormTemplateOption[] {
  return [
    {
      label: i18n.t('common.form.selectValue'),
      value: '',
    },
    ...accessFunctions
      .filter((accessFunction) => accessFunction.tag === 'SIEMENS_LIMS')
      .map((accessFunction) => {
        return {
          label: accessFunction.name,
          value: atob(accessFunction.id as string),
        };
      }),
  ];
}

function getCollectionFunctionOptions(
  collectionFunctions: CollectionFunction[],
): FormTemplateOption[] {

  return [
    {
      label: i18n.t('common.form.selectValue'),
      value: '',
    },
    ...collectionFunctions
      .filter((collectionFunction) => (collectionFunction.type === 'ETSI') || (collectionFunction.type === 'ETSIIP'))
      .map((collectionFunction) => {
        return {
          label: collectionFunction.name,
          value: atob(collectionFunction.id as string),
        };
      }),
  ];
}

export function getLeaOptions(
  afName: string,
  accessFunctions: AccessFunction[],
): FormTemplateOption[] {
  const af = accessFunctions.find((af) => af.name === afName);
  if (!af) {
    return [];
  }

  const leas: string[] = (af.dataInterfaces ?? [])
    .flatMap((df) => df.options)
    .filter((o) => ['lea', 'leacs', 'leaps'].includes(o.key))
    .map((o) => o.value as string);

  return [
    {
      label: i18n.t('common.form.selectValue'),
      value: '',
    },
    ...leas.map((lea) => {
      return {
        label: lea,
        value: lea,
      };
    }),
  ];
}
